import type { ComponentProps } from 'react';
import { useAuthStore } from '../../store/useAuthStore';
import { Avatar } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { ROLE_LABELS, asCollaboratorRole } from '../../services/auth/permissions';

type BadgeVariant = ComponentProps<typeof Badge>['variant'];

function getRoleBadgeVariant(role: string): BadgeVariant {
  switch (role) {
    case 'admin':
      return 'gold';
    case 'lawyer':
      // 'primary' (vert foncé translucide) disparaît sur le fond vert de la sidebar.
      return 'success';
    case 'secretary':
      return 'default';
    default:
      return 'default';
  }
}

function getRoleLabel(role: string): string {
  const typed = asCollaboratorRole(role as never);
  return typed ? ROLE_LABELS[typed] : role;
}

interface SidebarUserCardProps {
  collapsed: boolean;
}

export function SidebarUserCard({ collapsed }: SidebarUserCardProps) {
  const collaborator = useAuthStore((s) => s.collaborator);

  if (!collaborator) return null;

  if (collapsed) {
    return (
      <div className="flex items-center justify-center px-2 py-3">
        <Avatar
          name={collaborator.full_name}
          src={collaborator.avatar_url}
          size="md"
        />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 px-4 py-4">
      <Avatar
        name={collaborator.full_name}
        src={collaborator.avatar_url}
        size="md"
      />
      <div className="flex-1 min-w-0">
        <p
          className="text-sm font-semibold truncate"
          style={{ color: 'var(--cime-sidebar-text)' }}
        >
          {collaborator.full_name}
        </p>
        <Badge
          variant={getRoleBadgeVariant(collaborator.role)}
          className="mt-1"
        >
          {getRoleLabel(collaborator.role)}
        </Badge>
      </div>
    </div>
  );
}
